import React, { useEffect } from 'react';
import './PromotionalAnalysisControl.css';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import useDimensionOptions from '../../hooks/useDimensionOptions';
import { deriveGranularityFromLocation, deriveRegionFromLocation } from '../../utils_1/locationRouting';
import useSharedForecastFilters from '../../hooks/useSharedForecastFilters';
import { useAuth } from '../../context/AuthContext';
import { applyRoleLocks, getRoleLocks } from '../../utils_1/roleLocks';

const PromotionalAnalysisControl = ({ onPromotionalForecast, loading }) => {
  const [params, setParams] = useSharedForecastFilters([
    'promotional_analysis_filters',
    'daily_forecast_filters',
    'scenario_panel_filters',
  ]);
  const { user } = useAuth();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);
  const brandLocked = Boolean(roleLocks.lockedBrand);
  const channelLocked = Boolean(roleLocks.lockedChannel);

  // Standard promo type options based on the dataset event_type column
  const PROMO_TYPES = [
    { value: '',                       label: 'All Types' },
    { value: 'Monthly Promotion',      label: 'Monthly Promotion' },
    { value: 'Digital Campaign',       label: 'Digital Campaign' },
    { value: 'Flash Promotion',        label: 'Flash Promotion' },
    { value: 'Weekend Promotion',      label: 'Weekend Promotion' },
    { value: 'National Holiday',       label: 'National Holiday' },
    { value: 'Festival',               label: 'Festival' },
    { value: 'Regional Festival',      label: 'Regional Festival' },
  ];

  // Initialize dates to today and 28 days
  useEffect(() => {
    setParams(prev => {
      if (!prev.start_date) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + 1); // Default to tomorrow
        const endDate = new Date(startDate.getTime() + 27 * 24 * 60 * 60 * 1000);
        return {
          ...prev,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        };
      }
      return prev;
    });
  }, [setParams]);

  // Shared dimension options hook (brands, SKUs, channels, locations)
  const { options, selectedBrand, setSelectedBrand } = useDimensionOptions(
    brandLocked ? roleLocks.lockedBrand : (params.brand || '')
  );
  const displayBrand = brandLocked ? roleLocks.lockedBrand : params.brand;
  const brandOptions = brandLocked && roleLocks.lockedBrand && !options.brands.includes(roleLocks.lockedBrand)
    ? [roleLocks.lockedBrand, ...options.brands]
    : options.brands;
  const channelOptions = channelLocked && roleLocks.lockedChannel && !options.channels.includes(roleLocks.lockedChannel)
    ? [roleLocks.lockedChannel, ...options.channels]
    : options.channels;

  useEffect(() => {
    if (brandLocked) {
      if (selectedBrand !== roleLocks.lockedBrand) {
        setSelectedBrand(roleLocks.lockedBrand);
      }
      if (params.brand !== roleLocks.lockedBrand) {
        setParams(prev => ({ ...prev, brand: roleLocks.lockedBrand, sku_id: '' }));
      }
      return;
    }
    if (selectedBrand !== params.brand) {
      setParams(prev => ({ ...prev, brand: selectedBrand, sku_id: '' }));
    }
  }, [brandLocked, params.brand, roleLocks.lockedBrand, selectedBrand, setParams, setSelectedBrand]);

  useEffect(() => {
    setParams(prev => {
      const next = applyRoleLocks(prev, role);
      const unchanged = Object.keys(next).every(key => next[key] === prev[key]);
      return unchanged ? prev : next;
    });
  }, [role, setParams]);

  // Regional configuration
  const regionalConfigs = {
    'North': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'North Region - Daily forecast with separated promotional modeling'
    },
    'South': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'South Region - Daily forecast with separated promotional modeling'
    },
    'East': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'East Region - Daily forecast with separated promotional modeling'
    },
    'West': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'West Region - Daily forecast with separated promotional modeling'
    },
    'Central': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'Central Region - Daily forecast with separated promotional modeling'
    },
    'China': {
      defaultGranularity: 'monthly',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [30, 60, 90, 180],
      description: 'Monthly forecast with separate base and promotional analysis'
    },
    'Korea': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60],
      description: 'Daily forecast with separate promo/non-promo analysis'
    },
    'USA': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60, 90],
      description: 'Standard daily forecast with promotional separation'
    },
    'EU': {
      defaultGranularity: 'weekly',
      defaultPromoApproach: 'hybrid',
      forecastDaysOptions: [14, 28, 56, 84, 180],
      description: 'Weekly forecast with hybrid promotional modeling'
    },
    'Japan': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60],
      description: 'Daily forecast with separated promotional modeling'
    },
    'SEA': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'separated',
      forecastDaysOptions: [7, 14, 30, 60],
      description: 'Daily forecast with separated promotional modeling'
    },
    'LATAM': {
      defaultGranularity: 'daily',
      defaultPromoApproach: 'hybrid',
      forecastDaysOptions: [14, 28, 56, 84],
      description: 'Daily forecast with hybrid promotional modeling'
    }
  };

  const applyLocationRouting = (sourceParams) => {
    const derivedRegion = deriveRegionFromLocation(sourceParams.location);
    if (!derivedRegion) {
      return sourceParams;
    }

    const derivedConfig = regionalConfigs[derivedRegion] || regionalConfigs.USA;
    return {
      ...sourceParams,
      region: derivedRegion,
      promo_modeling_approach: derivedConfig.defaultPromoApproach,
      forecast_days: Math.min(
        sourceParams.forecast_days,
        Math.max(...derivedConfig.forecastDaysOptions)
      ),
    };
  };

  // Update regional settings when region changes
  useEffect(() => {
    const config = regionalConfigs[params.region];
    if (config) {
      setParams(prev => ({
        ...prev,
        promo_modeling_approach: config.defaultPromoApproach,
        forecast_days: Math.min(prev.forecast_days, Math.max(...config.forecastDaysOptions))
      }));
    }
  }, [params.region]); // eslint-disable-line react-hooks/exhaustive-deps

  // Location is the source of truth for region/granularity on the promotional page.
  useEffect(() => {
    if (!params.location) return;
    const derivedRegion = deriveRegionFromLocation(params.location);
    if (!derivedRegion || derivedRegion === params.region) return;

    setParams(prev => applyLocationRouting(prev));
  }, [params.location]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (param, value) => {
    if (param === 'start_date' || param === 'end_date') {
      const otherValue = param === 'start_date' ? params.end_date : params.start_date;
      if (value && otherValue) {
        const start = param === 'start_date' ? new Date(value) : new Date(otherValue);
        const end = param === 'end_date' ? new Date(value) : new Date(otherValue);
        if (start > end) {
          window.alert('Start date cannot be after end date.');
          return;
        }
      }
    }

    setParams(prev => {
      const newParams = { ...prev, [param]: value };

      // Interconnect Dates and Forecast Days
      if (param === 'start_date' || param === 'end_date') {
        if (newParams.start_date && newParams.end_date) {
          const start = new Date(newParams.start_date);
          const end = new Date(newParams.end_date);
          // Calculate difference in days (UTC)
          const diffTime = end - start;
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 0) {
            newParams.forecast_days = diffDays;
          }
        }
      } else if (param === 'location') {
        const derivedRegion = deriveRegionFromLocation(value);
        if (derivedRegion) {
          newParams.region = derivedRegion;
          newParams.forecast_granularity = deriveGranularityFromLocation(value);
        }
      } else if (param === 'forecast_days') {
        if (newParams.start_date && newParams.forecast_days) {
          const start = new Date(newParams.start_date);
          start.setUTCDate(start.getUTCDate() + parseInt(newParams.forecast_days));
          newParams.end_date = start.toISOString().split('T')[0];
        }
      }

      return newParams;
    });
  };

  const setQuickDate = (days) => {
    const start = new Date();
    start.setDate(start.getDate() + 1);
    const end = new Date(start.getTime() + (days - 1) * 24 * 60 * 60 * 1000);
    
    setParams(prev => ({
      ...prev,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
      forecast_days: days
    }));
  };


  const handleRegionChange = (region) => {
    setParams(prev => ({ ...prev, region }));
  };

  const currentConfig = regionalConfigs[params.region] || regionalConfigs['USA'];
  const requestParams = applyLocationRouting(params);

  return (
    <div className="control-panel card">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <h3 className="panel-title">🎯 Promotional Analysis</h3>
        <div className="preset-buttons">
          <span className="preset-label" style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 600 }}>Quick Select:</span>
          <button className="btn-preset" onClick={() => setQuickDate(7)}>7D</button>
          <button className="btn-preset" onClick={() => setQuickDate(14)}>14D</button>
          <button className="btn-preset" onClick={() => setQuickDate(30)}>30D</button>
          <button className="btn-preset" onClick={() => setQuickDate(90)}>90D</button>
        </div>
        <div className="region-selector">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Region <HelpTooltip content="Select the geographic region to apply regional forecasting presets." /></label>
          <select
            value={params.region || 'North'}
            onChange={(e) => handleRegionChange(e.target.value)}
            className="form-select region-select"
            disabled={Boolean(params.location)}
          >
            {Object.keys(regionalConfigs).map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
          <div className="field-note">
            {params.location ? 'Auto-derived from the selected location.' : 'You can choose a region manually until a location is selected.'}
          </div>
        </div>
      </div>

      {currentConfig && (
      <div className="regional-info">
        <div className="info-badge">
          <span className="badge-label">Active Configuration:</span>
          <span className="badge-value">{currentConfig.description}</span>
        </div>
      </div>
      )}

      <div className="tab-content">
        <div className="form-grid grid-3">
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Brand <HelpTooltip content="Select a brand to narrow down the SKU list below." /></label>
            <select
              value={displayBrand}
              onChange={(e) => { handleChange('brand', e.target.value); setSelectedBrand(e.target.value); }}
              className="form-input"
              disabled={brandLocked}
            >
              <option value="">All Brands (Optional)</option>
              {brandOptions.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>SKU ID <HelpTooltip content="Select a specific product to forecast, or choose All SKU IDs." /></label>
            <select
              value={params.sku_id}
              onChange={(e) => handleChange('sku_id', e.target.value)}
              className="form-input"
            >
              <option value="">All SKU IDs</option>
              {options.skus.map(sku => (
                <option key={sku} value={sku}>{sku}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Channel <HelpTooltip content="Select a specific sales channel, or choose All Channels." /></label>
            <select
              value={params.channel}
              onChange={(e) => handleChange('channel', e.target.value)}
              className="form-input"
              disabled={channelLocked}
            >
              <option value="">All Channels</option>
              {channelOptions.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Location <HelpTooltip content="Select a specific geographical location, or choose All Locations." /></label>
            <select
              value={params.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="form-input"
            >
              <option value="">All Locations</option>
              {options.locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Start Date <HelpTooltip content="The beginning date for the forecast." /></label>
            <input
              type="date"
              value={params.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>End Date <HelpTooltip content="The ending date for the forecast." /></label>
            <input
              type="date"
              value={params.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Forecast Days
              <span className="field-hint">({currentConfig?.defaultGranularity || 'daily'} granularity)</span>
              <HelpTooltip content="The default number of days to forecast based on the region." />
            </label>
            <select
              value={params.forecast_days}
              onChange={(e) => handleChange('forecast_days', parseInt(e.target.value))}
              className="form-select"
            >
              {currentConfig.forecastDaysOptions.map(days => (
                <option key={days} value={days}>
                  {days} days
                  {currentConfig.defaultGranularity === 'monthly' && ` (~${Math.ceil(days / 30)} months)`}
                  {currentConfig.defaultGranularity === 'weekly' && ` (~${Math.ceil(days / 7)} weeks)`}
                </option>
              ))}
              {/* Dynamic Option for Custom Days determined by Date Selection */}
              {!currentConfig.forecastDaysOptions.includes(params.forecast_days) && (
                <option key="custom" value={params.forecast_days}>
                  {params.forecast_days} days (Custom)
                </option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Forecast Granularity <HelpTooltip content="Choose the level of detail for the forecast output (e.g. daily, weekly, monthly)." /></label>
            <select
              value={params.forecast_granularity || 'daily'}
              onChange={(e) => handleChange('forecast_granularity', e.target.value)}
              className="form-input"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Promotional Modeling Approach */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Promotional Modeling <HelpTooltip content="Select how promotional effects are factored into the forecasted demand." /></label>
            <select
              value={params.promo_modeling_approach}
              onChange={(e) => handleChange('promo_modeling_approach', e.target.value)}
              className="form-select"
            >
              <option value="separated">Separated (Base + Promo)</option>
              <option value="embedded">Embedded (Combined)</option>
              <option value="hybrid">Hybrid (Adaptive)</option>
            </select>
            <div className="field-note">
              {params.promo_modeling_approach === 'separated' && 'Analyzes base and promotional demand separately'}
              {params.promo_modeling_approach === 'embedded' && 'Combines promotional effects into base forecast'}
              {params.promo_modeling_approach === 'hybrid' && 'Uses best approach based on data patterns'}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Promo Type Filter <HelpTooltip content="Filter promotions by type. Select 'All Types' to include all promotion categories." /></label>
            <select
              value={params.promo_type_filter}
              onChange={(e) => handleChange('promo_type_filter', e.target.value)}
              className="form-input"
            >
              {PROMO_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Conditional rendering based on promotional modeling approach */}
        {params.promo_modeling_approach === 'separated' && (
          <div className="form-group checkbox-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Include in Analysis <HelpTooltip content="Select which demand forecasts to include in the visual output." /></label>
            <div className="checkbox-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={params.include_base_demand}
                  onChange={(e) => handleChange('include_base_demand', e.target.checked)}
                  className="form-checkbox"
                />
                <span>Base Demand</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={params.include_promotional_demand}
                  onChange={(e) => handleChange('include_promotional_demand', e.target.checked)}
                  className="form-checkbox"
                />
                <span>Promotional Demand</span>
              </label>
            </div>
          </div>
        )}

        {params.promo_modeling_approach === 'embedded' && (
          <div className="embedded-note">
            <div className="note-content">
              <strong>Embedded Mode:</strong> Promotional effects are automatically included in the combined forecast.
              No separate base/promotional breakdown will be shown.
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            onClick={() => onPromotionalForecast(requestParams)}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Run Promotional Analysis'}
          </button>

          {params.region && (
            <div className="action-note">
              Using {params.region} regional configuration: {currentConfig.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionalAnalysisControl;
